import React, { useState, useMemo } from 'react';
import {
  BarChart3,
  Download,
  RefreshCw,
  Calendar,
  DollarSign,
  FileText,
  Filter,
  TrendingUp,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { useBusiness } from '@/contexts/BusinessContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers } from '@/hooks/useUsers';
import { useCompanies } from '@/hooks/useCompanies';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { reportsPageTour } from '@/walkthrough/tourSteps';
import {
  useGenerateReport,
  useExportCSV,
  getCurrentQuarterRange,
  getPreviousQuarterRange,
  getYTDRange,
  getQuarterLabel,
  type ReportData,
  type ReportFilters,
} from '@/hooks/useReports';
import { formatCurrency } from '@/services/projects';

type DatePreset = 'currentQuarter' | 'previousQuarter' | 'ytd' | 'custom';

export function ReportsPage() {
  const { currentBusiness, getBusinessLabel } = useBusiness();
  const { isOwnerOrAdmin, user } = useAuth();
  const { data: users } = useUsers();
  const { data: companies } = useCompanies();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(reportsPageTour);

  const generateReportMutation = useGenerateReport();
  const exportCSVMutation = useExportCSV();

  // Filter state
  const [datePreset, setDatePreset] = useState<DatePreset>('currentQuarter');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [salesRepFilter, setSalesRepFilter] = useState('');
  const [referralSourceFilter, setReferralSourceFilter] = useState('');
  const [commissionStatus, setCommissionStatus] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [mode, setMode] = useState<'closed' | 'pipeline'>('closed');

  // Report data
  const [reportData, setReportData] = useState<ReportData | null>(null);

  // Get date range based on preset
  const getDateRange = (): { start: Date; end: Date } => {
    switch (datePreset) {
      case 'currentQuarter':
        return getCurrentQuarterRange();
      case 'previousQuarter':
        return getPreviousQuarterRange();
      case 'ytd':
        return getYTDRange();
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : new Date(),
          end: customEndDate ? new Date(customEndDate + 'T23:59:59') : new Date(),
        };
    }
  };

  // Get reps for filter dropdown
  const reps = useMemo(() => {
    if (!users) return [];
    return users.filter((u) => u.role === 'rep' && u.status === 'active');
  }, [users]);

  // Handle report generation
  const handleGenerateReport = async () => {
    const { start, end } = getDateRange();

    const filters: ReportFilters = {
      business: currentBusiness,
      startDate: start,
      endDate: end,
      salesRepId: salesRepFilter || undefined,
      referralSourceId: referralSourceFilter || undefined,
      commissionStatus,
      mode,
      // Scope to the signed-in rep unless they're owner/admin (required so the
      // query passes Firestore rules; an unscoped rep query returns nothing).
      currentUserId: user?.id,
      isAdmin: isOwnerOrAdmin,
    };

    try {
      const data = await generateReportMutation.mutateAsync(filters);
      setReportData(data);
    } catch {
      // Error handled by mutation
    }
  };

  // Handle CSV export
  const handleExportCSV = () => {
    if (!reportData) return;

    const { start, end } = getDateRange();
    const filename = `commission-report-${start.toISOString().split('T')[0]}-to-${end.toISOString().split('T')[0]}.csv`;

    exportCSVMutation.mutate({ report: reportData, filename });
  };

  // Get date range label for display
  const getDateRangeLabel = (): string => {
    switch (datePreset) {
      case 'currentQuarter':
        return getQuarterLabel(new Date()) + ' (Current)';
      case 'previousQuarter': {
        const { start } = getPreviousQuarterRange();
        return getQuarterLabel(start);
      }
      case 'ytd':
        return `Year to Date (${new Date().getFullYear()})`;
      case 'custom':
        if (customStartDate && customEndDate) {
          return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
        }
        return 'Custom Range';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Commission Reports</h1>
        <p className="page-description">
          Generate and export commission reports for {getBusinessLabel(currentBusiness)}.
        </p>
      </div>

      {/* Filters */}
      <div data-tour="report-filters" className="card">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Filter size={20} />
          Report Filters
        </h2>

        <div data-tour="report-date-range" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Report mode: closed (earned) vs pipeline (potential) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report
            </label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as 'closed' | 'pipeline')}
              className="input"
            >
              <option value="closed">Earned (closed jobs)</option>
              <option value="pipeline">Potential (in-progress jobs)</option>
            </select>
          </div>

          {/* Date Range Preset */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Range {mode === 'pipeline' && <span className="text-gray-400">(ignored)</span>}
            </label>
            <select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value as DatePreset)}
              className="input"
            >
              <option value="currentQuarter">Current Quarter</option>
              <option value="previousQuarter">Previous Quarter</option>
              <option value="ytd">Year to Date</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {datePreset === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="input"
                />
              </div>
            </>
          )}

          {/* Sales Rep Filter */}
          {isOwnerOrAdmin && reps.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sales Rep
              </label>
              <select
                value={salesRepFilter}
                onChange={(e) => setSalesRepFilter(e.target.value)}
                className="input"
              >
                <option value="">All Reps</option>
                {reps.map((rep) => (
                  <option key={rep.id} value={rep.id}>
                    {rep.displayName || rep.email}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Referral Source Filter */}
          {companies && companies.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referral Source
              </label>
              <select
                value={referralSourceFilter}
                onChange={(e) => setReferralSourceFilter(e.target.value)}
                className="input"
              >
                <option value="">All Sources</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Commission Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commission Status
            </label>
            <select
              value={commissionStatus}
              onChange={(e) => setCommissionStatus(e.target.value as typeof commissionStatus)}
              className="input"
            >
              <option value="all">All</option>
              <option value="paid">Paid Only</option>
              <option value="unpaid">Unpaid Only</option>
            </select>
          </div>
        </div>

        {/* Generate Button */}
        <div data-tour="report-actions" className="mt-6 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleGenerateReport}
            disabled={generateReportMutation.isPending || (datePreset === 'custom' && (!customStartDate || !customEndDate))}
            className="btn btn-primary"
          >
            {generateReportMutation.isPending ? (
              <>
                <RefreshCw className="animate-spin" size={18} />
                Generating...
              </>
            ) : (
              <>
                <BarChart3 size={18} />
                Generate Report
              </>
            )}
          </button>

          {reportData && (
            <button
              onClick={handleExportCSV}
              disabled={exportCSVMutation.isPending}
              className="btn btn-secondary"
            >
              {exportCSVMutation.isPending ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Export CSV
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div data-tour="report-table">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <SummaryCard
              icon={FileText}
              label="Total Projects"
              value={reportData.summary.totalProjects.toString()}
              color="blue"
            />
            <SummaryCard
              icon={TrendingUp}
              label="Total Revenue"
              value={formatCurrency(reportData.summary.totalRevenue)}
              color="gray"
            />
            <SummaryCard
              icon={DollarSign}
              label={reportData.mode === 'pipeline' ? 'Potential Commission' : 'Total Commission'}
              value={formatCurrency(reportData.summary.totalCommission)}
              color="purple"
            />
            <SummaryCard
              icon={CheckCircle}
              label="Paid"
              value={formatCurrency(reportData.summary.paidCommission)}
              color="green"
            />
            <SummaryCard
              icon={Clock}
              label="Unpaid"
              value={formatCurrency(reportData.summary.unpaidCommission)}
              color="orange"
            />
          </div>

          {/* Report Info */}
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {getDateRangeLabel()}
            </span>
            <span>
              Generated: {reportData.generatedAt.toLocaleString()}
            </span>
          </div>

          {/* Projects Table */}
          {reportData.projects.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Close Date</th>
                    <th>Type</th>
                    <th>Revenue</th>
                    <th>Commission</th>
                    <th>Status</th>
                    <th>Rep</th>
                    <th>Referral Source</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.projects.map((project) => (
                    <tr key={project.id}>
                      <td className="font-medium text-gray-900">{project.name}</td>
                      <td>{project.closeDate?.toLocaleDateString() || '—'}</td>
                      <td>{project.projectType || '—'}</td>
                      <td className="font-mono">{formatCurrency(project.totalRevenue)}</td>
                      <td className="font-mono">{formatCurrency(project.expectedCommission)}</td>
                      <td>
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            project.commissionPaid
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {project.commissionPaid ? 'Paid' : 'Unpaid'}
                        </span>
                      </td>
                      <td>{project.salesRepName || '—'}</td>
                      <td>{project.referralSourceName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={3}>Totals</td>
                    <td className="font-mono">{formatCurrency(reportData.summary.totalRevenue)}</td>
                    <td className="font-mono">{formatCurrency(reportData.summary.totalCommission)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="card">
              <div className="empty-state">
                <BarChart3 size={48} className="empty-state-icon" />
                <p className="text-lg font-medium text-gray-900">No projects found</p>
                <p className="mt-1">
                  No projects match your filter criteria for this date range.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!reportData && !generateReportMutation.isPending && (
        <div className="card">
          <div className="empty-state">
            <BarChart3 size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">Ready to generate report</p>
            <p className="mt-1">
              Select your filters above and click "Generate Report" to view commission data.
            </p>
          </div>
        </div>
      )}

      <WelcomeModal
        pageName={reportsPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}

// ===========================================
// Summary Card Component
// ===========================================

interface SummaryCardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
}

function SummaryCard({ icon: Icon, label, value, color }: SummaryCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
    gray: 'bg-gray-50 text-gray-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-lg font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}
